import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { RefreshCcw, ScrollText } from "lucide-react";
import { useCallback } from "react";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { EmptyResourceState } from "@/components/custom/resources/empty-resource-state";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import type { AuditLogsSearch } from "@/routes/_authenticated/orgs/$organizationId/projects/$projectId/audit-logs";
import { useGetAuditLogFilterOptions, useGetAuditLogs } from "./api/hooks";
import { AuditLogFilters } from "./components/audit-log-filters";
import { AuditLogItem } from "./components/audit-log-item";

export const AuditLogsList = () => {
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const navigate = useNavigate();

  const { actions, sources, resourceTypes, dateRange } = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/audit-logs",
  });

  const handleFilterChange = useCallback(
    (updates: Partial<AuditLogsSearch>) => {
      navigate({
        to: ".",
        search: (prev: AuditLogsSearch) => ({ ...prev, ...updates }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate]
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  } = useGetAuditLogs({
    organizationId,
    projectId,
    actions,
    sources,
    resourceTypes,
    dateRange,
  });

  const { data: filterOptionsResponse } = useGetAuditLogFilterOptions({
    organizationId,
    projectId,
    actions,
    sources,
    resourceTypes,
    dateRange,
  });

  const items = data?.pages.flatMap((page) => page.data.items) || [];

  const hasActiveFilters =
    actions.length > 0 ||
    sources.length > 0 ||
    resourceTypes.length > 0 ||
    dateRange !== "all";

  return (
    <ListLayout
      description="View a complete history of actions performed in this project. Track who did what, when, and from where."
      title="Audit Logs"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <AuditLogFilters
            filterOptions={filterOptionsResponse?.data}
            filters={{ actions, sources, resourceTypes, dateRange }}
            onFilterChange={handleFilterChange}
          />
          <Button
            disabled={isRefetching}
            onClick={() => refetch()}
            size="icon"
            title="Refresh audit logs"
            variant="outline"
          >
            <RefreshCcw className={isRefetching ? "animate-spin" : ""} />
          </Button>
        </div>
        <div className="space-y-2">
          {items.length === 0 ? (
            <EmptyResourceState
              description={
                hasActiveFilters
                  ? "No audit logs match your current filters."
                  : "Audit logs will appear here as actions are performed in this project."
              }
              icon={ScrollText}
              resourceType={
                hasActiveFilters ? "matching audit logs" : "audit logs"
              }
            />
          ) : (
            <>
              {items.map((log) => (
                <AuditLogItem key={log.id} log={log} />
              ))}
              {hasNextPage === true && (
                <div className="flex justify-center pt-4">
                  <Button
                    disabled={isFetchingNextPage}
                    onClick={() => fetchNextPage()}
                    variant="outline"
                  >
                    {isFetchingNextPage ? "Loading..." : "Load More"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ListLayout>
  );
};
